import _ from 'lodash';
import { camelizeKeys } from 'humps';
import * as actionTypes from '../constants/actionTypes';
import * as actions from '../actions';
import { normalizeQuoteModel, normalizeStoneModel } from '../schemas';
import { getApiConfig, getMasterFabricatorId } from '../utilities';
import { saveFile } from '../utilities/backend'
import {loadingDialog_show, messageBox_Show} from "../actions";
import {updateJob} from "../components/JobBoard/JobBoardActions";

function setFeatures(fabricator, master = false) {
  let featureGroups = {};
  let transientFeatures = [];
  let transientFeatureGroups = [];

  _.each(fabricator.features, (feature) => {
    const nameParts = feature.name.split(' - ');
    const groupName = nameParts[0];
    const displayName = _.tail(nameParts).join(' - ').trim();

    feature.displayName = displayName || feature.name;

    let { group, displayedFeatures } = (featureGroups[groupName] || {});
    if (!group) {
      let groupFeatures = _.filter(fabricator.features, f => {
        let parts = f.name.split(' - ');
        return groupName == parts[0];
      });
      let anyVisible = groupFeatures.filter(gf => !gf.hideFromFrontEnd).length > 0;
      let anyEnabled = groupFeatures.filter(gf => gf.isEnabled).length > 0;

      group = {
        name: groupName,
        enabled: anyEnabled,
        visible: anyVisible,
        features: [],
        description: feature.description
      };

      transientFeatureGroups.push(group);
      displayedFeatures = {};
      featureGroups[groupName] = { group, displayedFeatures };
    }
    // All modifiable properties are store in the list of transient features. Transient features are committed
    // on a save action.
    const { id, fabricatorFeatureId, description, unit, flags, imageUrl, cost, enabled, locked, selectedUnit } = feature;
    let _enabled = enabled;
    if (typeof feature.hideFromFrontEnd !== 'undefined') {
      _enabled = !feature.hideFromFrontEnd;
    }
    feature.enabled = _enabled;
    let transientFeature = displayedFeatures[displayName];
    if (!transientFeature) {
      transientFeature = {
        id,
        fabricatorFeatureId,
        displayName,
        groupName,
        description,
        unit,
        unitOptions: [unit],
        flags,
        imageUrl,
        cost: cost !== undefined ? cost : feature.fabricatorCost,
        enabled: _enabled,
        locked: locked !== undefined ? locked : false,
        backingFeatures: [feature],
        selectedUnit,
        hideFromKitchenVisualizer: feature.hideFromKitchenVisualizer == "True"
      };
      transientFeatures.push(transientFeature);
      group.features.push(id);
      displayedFeatures[displayName] = transientFeature;
    } else {
      // Collect the values that differ only between features with the same name.
      transientFeature.unitOptions.push(unit);
      transientFeature.backingFeatures.push(feature);
    }
  });

  if (master) {
    dispatch = {
      type: actionTypes.SET_MASTER_FEATURES,
      payload: {
        masterFeatures: transientFeatures,
        masterFeatureGroups: transientFeatureGroups
      }
    };
  } else {
    var dispatch = {
      type: actionTypes.SET_FEATURES,
      payload: {
        transientFeatures,
        transientFeatureGroups
      }
    };
  }

  return dispatch;
}

function setFabricator(fabricator, master) {
  master = master || false;
  return function (dispatch) {
    dispatch(setFeatures(fabricator, master));

    const { id, name, features } = fabricator;

    return dispatch({
      type: actionTypes.FETCH_FABRICATOR,
      fabricator: { id, name, features }
    });
  };
}

/**
 * Take the quote data model returned by the GetNewQuoteData API and construct it into a fabricator object. This
 * is a temporary solution until there is a specific API created for the Dashboard.
 */
function createFabricator(result, entities, master) {
  master = master || false;
  return function (dispatch, getState) {
    return new Promise((resolve) => {
      const orderedBy = entities && entities.orderedBy ? entities.orderedBy[result] : null;
      if (orderedBy === null) {
        // TODO: Marcus@HV: ADD ERROR HANDLING
      }

      const fabricator = orderedBy && entities.fabricators ? entities.fabricators[orderedBy.fabricators[0]] : null;
      if (!fabricator) {
        // TODO: Marcus@HV: ADD ERROR HANDLING
      }

      // The features specified in the quote model are ordered via the Feature Creator. It's also possible that features
      // that are disabled on the server (Enabled field is set to False) will show up in the list of features that are
      // returned on the fabricator object. We want to overwrite the list of features on the fabricator object to
      // preserve the ordering and ignore disabled entries. Note that disabled relates to the Enabled field in the
      // server's FeatureDefinition, we use the HideFromFrontEnd field to determine whether or not the feature is
      // disabled in the Feature Creator.
      fabricator.features = orderedBy.features.map(id => entities.features[id]);

      // TODO: HACK: Marcus@HV: Until we get the enabled and locked feature flags via the API, we use the fake ones
      // currently set on transientFeature above on the features themselves. This allows transientFeatures to be
      // persisted with those fake values and not get trampled by the API call.
      _.forEach(fabricator.features, (feature, index) => {
        const featuresState = getState().features;
        if (featuresState && featuresState.transientFeatures) {
          const transientFeature = featuresState.transientFeatures[index] || {};
          feature.enabled = transientFeature.enabled;
          feature.locked = transientFeature.locked;
        }
      });

      // Replace the features passed down from the server with our structured version.
      resolve(dispatch(setFabricator(fabricator, master)));
    });
  };
}

let fabricatorApi = {};

function hasFabricatorApi(state) {
  const fabricatorId = state.fabricator.id;
  if (fabricatorId) {
    if (!fabricatorApi.id || (fabricatorApi.id !== fabricatorId)) {
      fabricatorApi = getApiConfig(`fabricators/${fabricatorId}`);
      fabricatorApi.id = fabricatorId;
    }
  }

  return fabricatorApi.id !== undefined && fabricatorApi.get !== undefined && fabricatorApi.post !== undefined;
}

function shouldFetchFabricator(state) {
  const { fabricator } = state;
  if (!fabricator.id) {
    return true;
  } else if (fabricator.fetching) {
    return false;
  }
  return fabricator.invalidated;
}

export function loadAndFetchFabricator() {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(actions.requireFabricatorAdmin())
      .then(() => {
        if (state.fabricator.invalidated || !state.features.loaded) {
          dispatch(actions.loadingDialog_show(true, 'Loading Project Scope', 'Please wait while we look up your project scope data.'))
            .then(() => dispatch(actions.fetchFabricator()))
            .then(() => dispatch(actions.loadingDialog_show(false)))
            .catch(console.error)
        } else {
          dispatch(actions.fetchFabricator()).catch(console.error);
        }
      }).catch(console.error);
  }
}

export function fetchFabricator() {
  return function (dispatch, getState) {
    return new Promise((resolve) => {
      const { auth, asCustomer, newScope } = getState();
      const { get } = getApiConfig();
      let fabricatorId = asCustomer.data ? asCustomer.data.fabricatorId : auth.fabricatorId;
      var tool = newScope.categoriesVisualizer;
      let masterFabId = getMasterFabricatorId(auth.masterFabricatorIds, tool);
      return get(`Api/Dashboard/GetNewQuoteData?jobId=0&type=Fabricator&hide=0&customerId=${masterFabId}`)
        .then(json => normalizeQuoteModel(json))
        .then(({ result, entities }) => {
          dispatch({
            type: actionTypes.UPDATE_MASTER_ENTITIES,
            payload: { result, entities }
          });

          dispatch(createFabricator(result, entities, true));
        }).then(() => {
          get(`Api/Dashboard/GetNewQuoteData?jobId=0&type=Fabricator&hide=0&customerId=${fabricatorId}`)
            .then(json => normalizeQuoteModel(json))
            .then(({ result, entities }) => {
              dispatch({
                type: actionTypes.UPDATE_ENTITIES,
                payload: { result, entities }
              });
              dispatch(createFabricator(result, entities, false)).then(resolve);
            });
        });
    });
  };
}

export function setActiveFeatureGroupName(name) {
  return {
    type: actionTypes.SET_ACTIVE_FEATURE_GROUP_NAME,
    name
  };
}

export function getFabricatorFromState(state) {
  const {
    entities: { key, orderedBy, fabricators }
  } = state;

  const root = (orderedBy[key] || {});
  return root.fabricators ? fabricators[root.fabricators[0]] : {};
}

export function updateFabricatorTransientField(key, value) {
  return (dispatch, getState) => {
    let state = getState();
    let fabricator = getFabricatorFromState(state);
    return dispatch(actions.updateTransientField(fabricator, key, value));
  }
}

export function authGoogleCalendar() {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const data = require('../../assets/js/google_public.json');
      const CLIENT_ID = data.web.client_id;
      const SCOPES = ["https://www.googleapis.com/auth/calendar", "https://www.googleapis.com/auth/calendar.readonly"];

      gapi.auth.authorize({
        'client_id': CLIENT_ID,
        'scope': SCOPES,
        'immediate': false
      }, handleAuthResult);

      function handleAuthResult(authResult) {
        if (authResult && !authResult.error) {
          resolve({
            result: authResult,
            state: getState()
          });
        } else {
          reject(authResult);
        }
      }
    });
  }
}

export function importFabricator(data) {
  return dispatch => {
    return new Promise(resolve => {
      const { post } = getApiConfig();
      post(data, "Api/Dashboard/ImportFabricators").then(resolve);
    }).catch(console.error);
  }
}

export function importFabricatorsZip(data) {
  return dispatch => {
    return new Promise(resolve => {
      const { post } = getApiConfig();
      post(data, "Api/export/PostFabricatorZip").then(resolve);
    }).catch(console.error);
  }
}

export function exportFabricator(fabricatorIds, combine) {
  return dispatch => {
    return new Promise(resolve => {
      const { get } = getApiConfig();

      get(`api/export/fabricatorzip/${fabricatorIds}/${combine}`)
        .then((res) => {
          const arrayBuffer = base64ToArrayBuffer(res.content);
          saveFile("application/zip", arrayBuffer, res.fileName)
        })
        .then(resolve);
    }).catch(console.error);
  }
}

export function base64ToArrayBuffer(base64) {
  const binaryString = window.atob(base64); // Comment this if not using base64
  const bytes = new Uint8Array(binaryString.length);
  return bytes.map((byte, i) => binaryString.charCodeAt(i));
}

/**
 * @param markUpLogic {object}
 * @param isGlobal {boolean}
 * @param job {object} // jobInterface
 * @return {boolean}
 */
export function saveMarkUpLogic(markUpLogic, isGlobal, job = null) {
  return async (dispatch, getState) => {
    if (!markUpLogic) {
      return false
    }

    dispatch(loadingDialog_show(true, "Saving...", isGlobal
      ? "Please wait while we are updating your changes."
      : "Please wait while we are updating the job."));

    const state = getState();
    const {auth, asCustomer} = state;
    const fabricatorId = asCustomer.data ? asCustomer.data.fabricatorId : auth.fabricatorId;
    const {post} = getApiConfig(isGlobal
      ? `fabricators/${fabricatorId}/global-markup`
      : `JobBoard/${job.id}/markup`
    );
    let stringifiedObject = JSON.stringify(markUpLogic);
    if (job && job.markUpLogic) {
      if (stringifiedObject === job.markUpLogic) {
        dispatch(loadingDialog_show(false));
        return false
      } else {
        stringifiedObject = JSON.stringify({...markUpLogic, isUpdatedInJobBoard: true});
      }
    }
    const posted = await post(stringifiedObject);
    console.log("posted", posted);
    if (posted && posted.__error__) {
      dispatch(messageBox_Show(true, 'error', 'Error', 'Mark Up update failed'))
    }
    if (job) {
      // const {get} = getApiConfig('public/getBvJobData/' + job.id);
      // const jobResponse = await get();
      dispatch(updateJob(job, {markUpLogic: stringifiedObject}));
    }
    dispatch(loadingDialog_show(false));
    return posted
  }
}

export const saveFabricatorSignedContract = (contractBlob, fabricatorId) => {
  const formData = new FormData();
  formData.append("files", contractBlob, contractBlob.name);
  return getApiConfig('Fabricators/' + fabricatorId + '/saveContract').post(formData)
}
